import type { State } from 'src/state';
import { createPersistentEmitter } from 'src/lib/Emitter';
import StateObserver from 'src/StateObserver';
import { getSceneManager, System, waitForItPromise } from 'src/lib/utils';
import { careActions, CareActionType } from './care.redux';
import { Care } from 'src/replicant/buruburu/care';
import { animate } from '@play-co/timestep-core/ui';
import { CareItem } from 'src/replicant/buruburu/care/care.ruleset';
import { CareItemEntity, EntityManager } from '../logic/EntityManager';
import platform from '@play-co/gcinstant';
import statePromise from 'src/lib/statePromise';
import { conversationActions } from '../conversation/conversation.redux';
import { Conversation } from 'src/replicant/conversation';
import { captureGenericError } from 'src/lib/sentry';
import * as Sentry from '@sentry/browser';
import { careActionAnimations, getCareItemPosition } from './care.config';
import { assertNever, clamp } from 'src/replicant/utils';
import Application from 'src/Application';
import { Pet } from 'src/replicant/buruburu/pet';
import { unlockMinigame } from '../minigames/minigames.sequence';
import { duration } from 'src/replicant/utils/duration';
import { Visit } from 'src/replicant/visit';
import { Position } from '../position/PositionModel';
import { CareItemController } from './CareItemController';
import { getFriendsPet, getPet } from '../pet';
import { endVisitSession } from '../visit/visit.sequence';
import { getTutorialController } from '../tutorial/TutorialController';

type CurrentCareAction = State['care']['currentCareAction'];
type CareSystemUpdateParams = {
  careActionType: CurrentCareAction['type'];
  careActionItem: CurrentCareAction['item'];
  isAsleep: boolean;
};

type CareSystemActionParams = CareSystemUpdateParams & {
  entityIds: string[];
  playerIds: string[];
  playerId: string;
  petEntityId: string;
  visitSession: { friendId: string };
};

export function careSystem(app: Application): System {
  const attributeAnimator = (id) =>
    animate(
      new Proxy(
        {},
        {
          get: (_: {}, p: PropertyKey) =>
            StateObserver.getState().pet[id].attributes[p],
          set(_: {}, p: PropertyKey, value: any): boolean {
            if (id === platform.playerID) {
              getPet().appearance.setAttributes({ [p]: value });
            } else {
              getFriendsPet().appearance.setAttributes({ [p]: value });
            }
            return true;
          },
        },
      ),
    );

  const handleCareActionUpdate = async (args: CareSystemUpdateParams) => {
    // Get player id and entity id.
    const playerId = platform.playerID;
    const petEntityId = EntityManager.getEntityId('pet', playerId);

    // Create array of ids.
    const entityIds = [petEntityId];
    const playerIds = [playerId];

    // If there's a visit session add friend id to array.
    const { visitSession } = StateObserver.getState().user.visit;
    if (visitSession) {
      const friendPlayerId = visitSession.friendId;
      playerIds.push(friendPlayerId);
      entityIds.push(EntityManager.getEntityId('pet', friendPlayerId));
    }

    // Create params.
    const params = {
      ...args,
      entityIds,
      playerIds,
      playerId,
      petEntityId,
      visitSession,
    };

    // Handle actions based on type.
    const type = args.careActionType;

    // No action. Go to the previous scene.
    if (!type) {
      return handleNoCareActions(params);
    }

    // Update current care action.
    StateObserver.dispatch(
      careActions.setCareAction({ type, item: args.careActionItem }),
    );

    // Handle pet evolution.
    if (type === 'evolve') {
      return handleEvolveActions(params);
    }

    // Handle ascension
    if (type === 'ascension') {
      return handleAscensionActions(params);
    }

    // Hygiene actions. Will change scene and be executed one pet at a time.
    if (type === 'bath' || type === 'toilet') {
      return handleHygieneActions(params);
    }

    // Care actions that involve items.
    if (type === 'food' || type === 'snack' || type === 'firstAid') {
      return handleCareItemActions(params);
    }

    // Special case of items. Since only one item is instantiated when visiting a friend.
    if (type === 'toy') {
      return handleToyActions(params);
    }

    // Care action not supported.
    assertNever(type);
  };

  async function handleEvolveActions({
    careActionType: type,
    careActionItem: item,
    visitSession,
  }: CareSystemActionParams) {
    const pet = getPet();
    const friendPet = getFriendsPet();

    // Move all pets to the care action position.

    await Promise.all([
      pet.moveTo(getPetCarePosition(type, !!visitSession)),
      friendPet?.moveTo(getFriendPetCarePosition(type)),
    ]);

    // Care action was updated while waiting, so bail out
    const { currentCareAction } = StateObserver.getState().care;
    if (currentCareAction.type !== type || currentCareAction.item !== item) {
      return;
    }

    // Start care animation.
    pet.appearance.setAction(careActionAnimations[type]);

    // Normal care actions take 2s, while visiting the time is shortened to 1.5s.
    const delay = duration({ seconds: visitSession ? 1.5 : 2 });

    // Otherwise wait for a fixed amount of time.
    await waitForItPromise(delay);

    // Wait one more second if not visiting a friend.
    if (!visitSession) {
      await waitForItPromise(duration({ seconds: 1 }));
    }

    await postCareActionSequence(visitSession);

    await getTutorialController().handleEvent('petEvolved');
  }

  /**
   * Death action
   */
  async function handleAscensionActions({
    careActionType: type,
    careActionItem: item,
    visitSession,
  }: CareSystemActionParams) {
    const pet = getPet();

    await pet.moveTo(getPetCarePosition(type, !!visitSession));

    // Care action was updated while waiting, so bail out
    const { currentCareAction } = StateObserver.getState().care;
    if (currentCareAction?.type !== type || currentCareAction?.item !== item) {
      return;
    }

    pet.appearance.setAction(careActionAnimations[type]);

    // Normal care actions take 2s
    const delay = duration({ seconds: 2 });

    await waitForItPromise(delay);
  }

  async function handleHygieneActions({
    careActionType: type,
    careActionItem: item,
    visitSession,
  }: CareSystemActionParams) {
    const pet = getPet();
    const friendPet = getFriendsPet();

    // Move all pets to the care action position.

    await Promise.all([
      pet.moveTo(getPetCarePosition(type, !!visitSession)),
      friendPet?.moveTo(getFriendPetCarePosition(type)),
    ]);

    // Care action was updated while waiting, so bail out
    const { currentCareAction } = StateObserver.getState().care;
    if (currentCareAction.type !== type || currentCareAction.item !== item) {
      return;
    }

    pet.appearance.setFlipped(false);

    // Start care animation.
    pet.appearance.setAction(careActionAnimations[type]);
    friendPet?.appearance.setAction(null);

    // Normal care actions take 2s, while visiting the time is shortened to 1.5s.
    const delay = duration({ seconds: visitSession ? 1.5 : 2 });

    await waitForItPromise(delay);

    // If action requires swapping pets, do it.
    if (visitSession) {
      // Start by hanging the pet.
      pet.appearance.setAction('hang');
      friendPet?.appearance.setAction('hang');

      await waitForItPromise(500);

      // Swap positions.
      await Promise.all([
        pet.moveTo(getFriendPetCarePosition(type)),
        friendPet?.moveTo(getPetCarePosition(type, !!visitSession)),
      ]);

      friendPet?.appearance.setFlipped(false);

      // Play animation.
      pet.appearance.setAction(null);
      friendPet?.appearance.setAction(careActionAnimations[type]);

      // Await for animation to finish.
      await waitForItPromise(delay);
    }

    // Wait one more second if not visiting a friend.
    if (!visitSession) {
      await waitForItPromise(duration({ seconds: 1 }));
    }

    await postCareActionSequence(visitSession);
  }

  async function handleCareItemActions({
    careActionType: type,
    careActionItem: item,
    playerId,
    playerIds,
    visitSession,
  }: CareSystemActionParams) {
    const pet = getPet();
    const friendPet = getFriendsPet();

    // Move all pets to the care action position.
    const positionUpdates = [
      pet.moveTo(getPetCarePosition(type, !!visitSession)),
      friendPet?.moveTo(getFriendPetCarePosition(type)),
    ];

    // Get care item for current care action.
    const careItem = Care.ruleset.careItems[item];

    // Create items.
    const careItemCtrls: Record<string, CareItemController> = {};
    playerIds.forEach((id) => {
      const careItemEntityId = EntityManager.getEntityId(
        'careItem',
        careItem.id,
        id,
      );
      const controller = new CareItemController(
        getSceneManager(),
        EntityManager.createOrGetEntity(careItemEntityId) as CareItemEntity,
      );
      careItemCtrls[id] = controller;
      positionUpdates.push(
        controller.position.moveTo(
          getCareItemPosition(careItem, !!visitSession, id !== playerId),
          0,
        ),
      );
    });

    // Await until everything is in position.
    await Promise.all(positionUpdates);

    Object.values(careItemCtrls).forEach((controller) =>
      controller.appearance.animate(),
    );

    // Care action was updated while waiting, so bail out
    const { currentCareAction } = StateObserver.getState().care;
    if (currentCareAction.type !== type || currentCareAction.item !== item) {
      return;
    }

    // Start care animation.
    pet.appearance.setAction(careActionAnimations[type]);
    pet.appearance.setFlipped(!!visitSession);
    friendPet?.appearance.setAction(careActionAnimations[type]);
    friendPet?.appearance.setFlipped(false);

    // If action involves a care item, wait for UI to update, and clear the item.
    await tweenAttributes(careItem).then(() => {
      Object.values(careItemCtrls).forEach((controller) =>
        controller.destroy(),
      );

      pet.appearance.setAction(null);
      friendPet?.appearance.setAction(null);
    });

    // Wait one more second if not visiting a friend.
    if (!visitSession) {
      await waitForItPromise(duration({ seconds: 1 }));
    }

    await postCareActionSequence(visitSession);
  }

  async function handleToyActions({
    careActionType: type,
    careActionItem: item,
    playerId,
    visitSession,
  }: CareSystemActionParams) {
    const pet = getPet();
    const friendPet = getFriendsPet();

    // Move all pets to the care action position.
    const positionUpdates = [
      pet.moveTo(getPetCarePosition(type, !!visitSession)),
      friendPet?.moveTo(getFriendPetCarePosition(type)),
    ];

    // Get care item for current care action.
    const careItem = Care.ruleset.careItems[item];

    // Create item.
    const careItemEntityId = EntityManager.getEntityId(
      'careItem',
      careItem.id,
      playerId,
    );
    const careItemCtrl = new CareItemController(
      getSceneManager(),
      EntityManager.createOrGetEntity(careItemEntityId) as CareItemEntity,
    );
    positionUpdates.push(
      careItemCtrl.position.moveTo(
        getCareItemPosition(careItem, !!visitSession),
        0,
      ),
    );

    // Await until everything is in position.
    await Promise.all(positionUpdates);

    careItemCtrl.appearance.animate();

    // Care action was updated while waiting, so bail out
    const { currentCareAction } = StateObserver.getState().care;
    if (currentCareAction.type !== type || currentCareAction.item !== item) {
      return;
    }

    // Start care animation.
    pet.appearance.setAction(careActionAnimations[type]);
    pet.appearance.setFlipped(!!visitSession);
    friendPet?.appearance.setAction(careActionAnimations[type]);
    friendPet?.appearance.setFlipped(false);

    // If action involves a care item, wait for UI to update, and clear the item.
    await tweenAttributes(careItem).then(() => {
      void careItemCtrl.position.moveTo(null);
      pet.appearance.setAction(null);
      friendPet?.appearance.setAction(null);
    });

    // Wait one more second if not visiting a friend.
    if (!visitSession) {
      await waitForItPromise(duration({ seconds: 1 }));
    }

    await postCareActionSequence(visitSession);
  }

  async function handleNoCareActions({
    isAsleep,
    visitSession,
    playerId,
  }: CareSystemActionParams) {
    if (!app.isInit) {
      return;
    }

    // TODO prevent sleep transition during minigame
    const position: Position = isAsleep
      ? await handleSleepTransition()
      : {
          scene: visitSession ? 'friendsLivingRoom' : 'livingRoom',
          location: 'c',
        };

    const pet = getPet();
    const friendPet = getFriendsPet();

    const positionPromises = [];

    positionPromises.push(
      pet.moveTo({
        ...position,
        location: visitSession && !isAsleep ? 'lm' : position.location,
      } as any),
    );
    if (!isAsleep) {
      positionPromises.push(
        friendPet?.moveTo({
          ...position,
          location: visitSession ? 'rm' : position.location,
        } as any),
      );
    }

    await Promise.all(positionPromises);

    await waitForItPromise();

    pet.appearance.setAction(null);
    friendPet?.appearance.setAction(null);

    if (visitSession && isAsleep) {
      await endVisitSession(visitSession.friendId, false);
    }

    // sync game attributes state with player state after care action finished
    const { user } = StateObserver.getState();
    const attributes = Pet.getters(user).getAllAttributeValues(
      StateObserver.now(),
    );

    pet.appearance.setAttributes(attributes);
  }

  async function postCareActionSequence(
    visitSession: CareSystemActionParams['visitSession'],
  ) {
    StateObserver.dispatch(careActions.setCareAction(null));

    // Should not trigger unlocks if visiting a friend.
    if (visitSession) return;

    await unlockMinigame();
  }

  async function handleSleepTransition(): Promise<Position> {
    // Skip the sleep conversation if app is still initialising
    if (app.isInit) {
      const { user } = StateObserver.getState();
      const { getValidDialogs } = Conversation.getters(user);
      const { getCurrentEvolution, getPetState } = Pet.getters(user);
      const { isVisiting } = Visit.getters(user);

      const dialogOptions = getValidDialogs(
        'normal_chat',
        getCurrentEvolution().species,
        getPetState(StateObserver.now(), isVisiting()),
        user.conversation.answers,
      );
      if (dialogOptions.length === 1) {
        const dialog = dialogOptions[0];
        StateObserver.dispatch(
          conversationActions.setActiveDialog({
            id: platform.playerID,
            payload: dialog.id,
          }),
        );
        await statePromise(
          ({ conversation }) => !conversation[platform.playerID]?.activeDialog,
        );
      } else {
        Sentry.addBreadcrumb({
          category: 'debug',
          level: 'warning',
          data: { dialogOptions },
        });
        captureGenericError(
          `Multiple dialog options for 'sleeping' state`,
          null,
        );
      }
    }

    return {
      scene: 'bedroom',
      location: 'bed',
    };
  }

  function getPetCarePosition(
    actionType: CareActionType,
    isVisitingFriend: boolean = false,
  ): Position {
    switch (actionType) {
      case 'toy':
      case 'firstAid':
      case 'ascension':
      case 'evolve': {
        return {
          scene: isVisitingFriend ? 'friendsLivingRoom' : 'livingRoom',
          location: !isVisitingFriend ? 'c' : 'lm',
        };
      }
      case 'snack':
      case 'food': {
        return {
          scene: 'diningRoom',
          location: !isVisitingFriend ? 'mat' : 'matPlayer',
        };
      }
      case 'toilet': {
        return {
          scene: 'toiletRoom',
          location: 'toilet',
        };
      }
      case 'bath': {
        return {
          scene: 'bathroom',
          location: 'bath',
        };
      }
    }
    assertNever(actionType);
  }

  function getFriendPetCarePosition(actionType: CareActionType): Position {
    switch (actionType) {
      case 'toy':
      case 'firstAid':
      case 'ascension':
      case 'evolve': {
        return {
          scene: 'friendsLivingRoom',
          location: 'rm',
        };
      }
      case 'snack':
      case 'food': {
        return {
          scene: 'diningRoom',
          location: 'matFriend',
        };
      }
      case 'toilet': {
        return {
          scene: 'toiletRoom',
          location: 'side',
        };
      }
      case 'bath': {
        return {
          scene: 'bathroom',
          location: 'side',
        };
      }
    }
    assertNever(actionType);
  }

  function tweenAttributes(careItem: CareItem) {
    const { pet } = StateObserver.getState();
    const promises = Object.keys(pet).map((id) =>
      attributeAnimator(id)
        .clear()
        .now(getNewAttributes(id, careItem), 3000, animate.linear)
        .toPromise(),
    );

    return Promise.all(promises);
  }

  function getNewAttributes(id: string, careItem: CareItem) {
    const { pet } = StateObserver.getState();
    const { petAttributes, decayMultipliers, attributeBounds } = Pet.ruleset;

    const currentAttributes = petAttributes.reduce((acc, attribute) => {
      acc[attribute] = pet[id].attributes[attribute];
      return acc;
    }, {});

    return petAttributes.reduce(
      (acc, attribute) => {
        const careItemAmount = careItem[attribute];
        if (careItemAmount) {
          acc[attribute] = clamp(
            acc[attribute] - decayMultipliers[attribute] * careItemAmount,
            attributeBounds.min,
            attributeBounds.max,
          );
        }
        return acc;
      },
      { ...currentAttributes },
    );
  }

  const emitter = createPersistentEmitter(({ care, pet, ui }) => ({
    careActionType: care.currentCareAction?.type,
    careActionItem: care.currentCareAction?.item,
    isAsleep:
      pet[platform.playerID].statusEffects.asleep &&
      ui.transition.nextScene !== 'minigame',
  }));
  return {
    start: () => emitter.addListener(handleCareActionUpdate),
    stop: () => emitter.removeListener(handleCareActionUpdate),
  };
}
